import React from 'react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES, MAX_WIDTH_OPTIONS } from '../common/commonDefaults';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getRichTextExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Layout
      </h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Content Width</label>
        <div className="grid grid-cols-3 gap-1.5">
          {MAX_WIDTH_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange({ maxWidth: option.value })}
              className={`py-1.5 rounded text-sm font-medium transition-colors ${
                content.maxWidth === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  ),
});

/**
 * RichTextBlockEditor - Editor component for rich-text block
 */
const RichTextBlockEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const features = BLOCK_FEATURES['rich-text'];
  const extras = getRichTextExtraStyleControls({ content, onChange });

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading (optional)
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Enter heading..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Body Content */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Content
        </label>
        <textarea
          value={content.body || ''}
          onChange={(e) => handleChange('body', e.target.value)}
          placeholder="Enter your content..."
          rows={8}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          Use blank lines for paragraphs
        </p>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            extraControls={extras.sizeSpacingExtras}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            theme={theme || {}}
            blockType="rich-text"
          />
        </>
      )}
    </div>
  );
};

export default RichTextBlockEditor;
